import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { InfoOutlined } from '@mui/icons-material';
import { ShieldCheck, Pencil } from 'lucide-react';
import QRCode from 'react-qr-code';
import { ReactComponent as CopyIcon } from '../../../../assets/icons/copy.svg';
import './styles.scss';
import l from '../../../../lang';
import Dialog from '../../../common/dialog';
import Input from '../../../common/input';
import ButtonCustom from '../../../common/__deprecated__/button-custom';
import MessageError from '../../../common/message-error';
import Loading from '../../../common/loading';
import Switch from '../../../common/switch';
import { setupTwoFactor, confirmTwoFactor, disableTwoFactor, cleanTwoFactorState } from '../../../../store/profile/actions';
import { useDashboard } from '../../../../store/dashboard';

const TwoFactorModalShieldIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#4A7CF7"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const TwoFactorAuth = ({
  twoFactorLoading,
  twoFactorError,
  twoFactorErrorInfo,
  twoFactorSetup,
  twoFactorEnabled,
  userMe,
  fnDisableTwoFactor,
  fnSetupTwoFactor,
  fnConfirmTwoFactor,
  fnCleanTwoFactor,
  layout,
}) => {
  const { showTwoFactorAuthSetup } = useDashboard();
  const [open, setOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [copied, setCopied] = useState(false);

  const isEnabled = Boolean(userMe?.totpEnabled ?? twoFactorEnabled);
  const isConfigured = Boolean(userMe?.totpConfigured ?? twoFactorSetup?.secret);

  useEffect(() => {
    if (showTwoFactorAuthSetup) {
      handleOpen();
    }
  }, [showTwoFactorAuthSetup]);

  useEffect(() => {
    if (open && isEnabled) {
      setOpen(false);
      setCode('');
      setCopied(false);
      fnCleanTwoFactor();
    }
  }, [isEnabled, open, fnCleanTwoFactor]);

  useEffect(() => {
    if (disableOpen && !isEnabled) {
      setDisableOpen(false);
      setDisableCode('');
      fnCleanTwoFactor();
    }
  }, [isEnabled, disableOpen, fnCleanTwoFactor]);

  const handleOpen = () => {
    setOpen(true);
    setCode('');
    setCopied(false);
    fnCleanTwoFactor();
    fnSetupTwoFactor();
  };

  const handleClose = () => {
    setOpen(false);
    setCode('');
    setCopied(false);
    fnCleanTwoFactor();
  };

  const handleDisableOpen = () => {
    setDisableOpen(true);
    setDisableCode('');
    fnCleanTwoFactor();
  };

  const handleDisableClose = () => {
    setDisableOpen(false);
    setDisableCode('');
    fnCleanTwoFactor();
  };

  const handleCopy = async () => {
    if (!twoFactorSetup?.secret) {
      return;
    }

    try {
      await navigator.clipboard.writeText(twoFactorSetup.secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      setCopied(false);
    }
  };

  const handleConfirm = () => {
    if (!code) {
      return;
    }
    fnConfirmTwoFactor(code.trim());
  };

  const handleDisableConfirm = () => {
    if (!disableCode) {
      return;
    }
    fnDisableTwoFactor(disableCode.trim());
  };

  const handleToggle = () => {
    if (isEnabled) {
      handleDisableOpen();
    } else {
      handleOpen();
    }
  };

  return (
    <div className={`two-factor ${layout === 'account' ? 'two-factor--account' : ''}`}>
      {layout === 'account' ? (
        <>
          <div className="two-factor__strip two-factor__strip--toolbar">
            <div className="two-factor__strip-head">
              <ShieldCheck className="two-factor__strip-icon" size={22} strokeWidth={1.35} aria-hidden />
              <h3 className="two-factor__strip-title">{l('twoFactor.title')}</h3>
            </div>
            <div className="two-factor__strip-actions">
              {!isEnabled && (
                <button type="button" className="two-factor__outline-btn" onClick={handleOpen}>
                  <Pencil className="two-factor__outline-btn-icon" size={16} strokeWidth={1.35} aria-hidden />
                  {l('profilePage.configure2faCta')}
                </button>
              )}
            </div>
          </div>
          <div className="two-factor__account-body">
            <div className="two-factor__account-main">
              <div className="two-factor__account-field-label">{l('twoFactor.statusLabel')}</div>
              <div className="two-factor__account-field-value">
                {isEnabled ? l('twoFactor.status.enabled') : l('twoFactor.status.disabled')}
              </div>
            </div>
            <div className="two-factor__account-switch">
              <Switch checked={isEnabled} onChange={handleToggle} />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="two-factor__section">
            <div className="two-factor__section__title">{l('twoFactor.title')}</div>
          </div>
          <div className="two-factor__status">
            <span className="two-factor__status__label">{l('twoFactor.statusLabel')}</span>
            <span className="two-factor__status__value">
              {isEnabled ? l('twoFactor.status.enabled') : l('twoFactor.status.disabled')}
            </span>
            <div className="two-factor__status__switch">
              <Switch checked={isEnabled} onChange={handleToggle} />
            </div>
          </div>
          {!isEnabled && (
            <div className="two-factor__actions">
              <ButtonCustom
                text={isConfigured ? l('twoFactor.resumeButton') : l('twoFactor.setupButton')}
                onClick={handleOpen}
              />
            </div>
          )}
        </>
      )}
      {open && (
        <Dialog
          type="sm"
          fullScreenMobile
          dividers={false}
          unpaddingContent
          title={
            <span className="two-factor__dialog-title">
              <TwoFactorModalShieldIcon />
              <span>{l('twoFactor.title')}</span>
            </span>
          }
          handleClose={handleClose}
          body={
            <div className="two-factor__modal two-factor__modal--setup">
              {twoFactorLoading && !twoFactorSetup && (
                <div className="two-factor__loading">
                  <Loading />
                </div>
              )}
              {twoFactorError && !twoFactorSetup && (
                <div className="two-factor__error">
                  <MessageError errorInfo={twoFactorErrorInfo} />
                </div>
              )}
              {twoFactorSetup && (
                <>
                  <div className="two-factor__modal-panel">
                    <div className="two-factor__instructions">{l('twoFactor.modal.qrInstruction')}</div>
                    <div className="two-factor__qr">
                      <QRCode value={twoFactorSetup.otpAuthUrl} size={160} />
                    </div>
                    <div className="two-factor__secret">
                      <div className="two-factor__secret__label">{l('twoFactor.modal.manualCode')}</div>
                      <div className="two-factor__secret__row">
                        <div className="two-factor__secret__value">{twoFactorSetup.secret}</div>
                        <div className="two-factor__secret__copy-wrap">
                          {copied && (
                            <span className="two-factor__secret__copied-tooltip" role="status">
                              {l('twoFactor.modal.copied')}
                            </span>
                          )}
                          <button type="button" className="two-factor__secret__copy" onClick={handleCopy}>
                            <CopyIcon className="two-factor__secret__copy-icon" aria-hidden />
                            {l('twoFactor.modal.copy')}
                          </button>
                        </div>
                      </div>
                    </div>
                    <Input
                      name="twoFactorCode"
                      label={l('twoFactor.modal.codeLabel')}
                      value={code}
                      maxLength={6}
                      onChange={e => setCode(e.target.value)}
                    />
                    <div className="two-factor__helper">
                      <InfoOutlined className="two-factor__helper__icon" fontSize="small" aria-hidden />
                      <span>{l('twoFactor.modal.helper')}</span>
                    </div>
                    {twoFactorError && (
                      <div className="two-factor__error">
                        <MessageError errorInfo={twoFactorErrorInfo} />
                      </div>
                    )}
                  </div>
                  <div className="confirm-footer-container two-factor__buttons two-factor__buttons--setup">
                    <ButtonCustom text={l('twoFactor.modal.cancel')} size="half-left-square" onClick={handleClose} />
                    <ButtonCustom
                      text={l('twoFactor.modal.confirm')}
                      variant="contained"
                      size="half-right-square"
                      onClick={handleConfirm}
                      disabled={twoFactorLoading || !code}
                    />
                  </div>
                </>
              )}
            </div>
          }
        />
      )}
      {disableOpen && (
        <Dialog
          type="sm"
          fullScreenMobile
          dividers={false}
          unpaddingContent
          title={l('twoFactor.disable.title')}
          handleClose={handleDisableClose}
          body={
            <div className="two-factor__modal two-factor__modal--disable">
              <div className="two-factor__instructions">{l('twoFactor.disable.helper')}</div>
              <Input
                name="twoFactorDisableCode"
                label={l('twoFactor.disable.codeLabel')}
                value={disableCode}
                maxLength={6}
                onChange={e => setDisableCode(e.target.value)}
              />
              {twoFactorError && (
                <div className="two-factor__error">
                  <MessageError errorInfo={twoFactorErrorInfo} />
                </div>
              )}
              <div className="confirm-footer-container two-factor__buttons two-factor__buttons--disable">
                <ButtonCustom text={l('twoFactor.disable.cancel')} size="half-left-square" onClick={handleDisableClose} />
                <ButtonCustom
                  text={l('twoFactor.disable.confirm')}
                  variant="contained"
                  size="half-right-square"
                  onClick={handleDisableConfirm}
                  disabled={twoFactorLoading || !disableCode}
                />
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

TwoFactorAuth.propTypes = {
  twoFactorLoading: PropTypes.bool,
  twoFactorError: PropTypes.bool,
  twoFactorErrorInfo: PropTypes.object,
  twoFactorSetup: PropTypes.object,
  twoFactorEnabled: PropTypes.bool,
  userMe: PropTypes.object,
  fnDisableTwoFactor: PropTypes.func,
  fnSetupTwoFactor: PropTypes.func,
  fnConfirmTwoFactor: PropTypes.func,
  fnCleanTwoFactor: PropTypes.func,
  layout: PropTypes.oneOf(['default', 'account']),
};

TwoFactorAuth.defaultProps = {
  layout: 'default',
};

const mapStateToProps = state => ({
  twoFactorLoading: state.profile.twoFactorLoading,
  twoFactorError: state.profile.twoFactorError,
  twoFactorErrorInfo: state.profile.twoFactorErrorInfo,
  twoFactorSetup: state.profile.twoFactorSetup,
  twoFactorEnabled: state.profile.twoFactorEnabled,
  userMe: state.dashboard.userMe,
});

const mapDispatchToProps = dispatch => ({
  fnSetupTwoFactor: () => dispatch(setupTwoFactor()),
  fnConfirmTwoFactor: code => dispatch(confirmTwoFactor(code)),
  fnDisableTwoFactor: code => dispatch(disableTwoFactor(code)),
  fnCleanTwoFactor: () => dispatch(cleanTwoFactorState()),
});

export default connect(mapStateToProps, mapDispatchToProps)(TwoFactorAuth);
